import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { calculateExchangeRate } from '../hooks/hooks';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(8),
  padding: theme.spacing(4),
//   display: 'flex',
//   flexDirection: 'column',
//   alignItems: 'center',
}));

const FormContainer = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
    elevation:0,
    borderRadius: "3px",
    textTransform: 'none',
    textTransform:"uppercase",
    fontSize: 12,
    fontWeight:600,
    boxShadow:"none",
    backgroundColor:"#D32F2F",                  
    "&:hover": {
        boxShadow: "none",
    },
}));

// Currency options
const currencies = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'ZAR', name: 'South African Rand' },
];

// Validation schema
const validationSchema = Yup.object().shape({
  source_currency: Yup.string().required('Source currency is required'),
  destination_currency: Yup.string()
    .required('Destination currency is required')
    .notOneOf([Yup.ref('source_currency')], 'Source and destination currencies must be different'),
  destination_amount: Yup.number()
    .required('Amount is required')
    .positive('Amount must be positive')
    .typeError('Amount must be a number'),
});


const ExchangeRates = () => {
  const [loading, setLoading] = React.useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [exchangeRates, setExchangeRates] = useState('')
    const queryClient = useQueryClient();


     const mutation = useMutation({
        mutationFn:calculateExchangeRate,
        onSuccess: (response) => {
          queryClient.invalidateQueries('sms');
          setLoading(false)
          setSnackbarMessage("Exchange request sent successfully!");
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          setExchangeRates(response.data)
          console.log(response.data)
        },
        onError: (error) => {
          // console.log('Error creating sms:', error);
          setLoading(false)
          setSnackbarMessage(error.response.data.message || 'Failed to send exchange request. Please try again.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }
      });

       const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
      };
    


  const formik = useFormik({
    initialValues: {
      source_currency: 'KES',
      destination_currency: 'TZS',
      destination_amount: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const payload = {
        source_currency: values.source_currency,
        destination_currency: values.destination_currency,
        destination_amount: values.destination_amount
      };
      mutation.mutate(payload);
    },
  });
  return ( 
  <Box>
    <Box maxWidth={500} sx={{
        p:2,
        position:"relative"
        }}
      >
      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        // anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
            maxWidth: '100%',
            position: 'static', // Disable fixed positioning
            mt: 1, // Add margin to separate from other elements
            mb: 2, // Optional: Add bottom margin
        }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <StyledPaper elevation={0}>
        <Typography variant="h5" mb={2} sx={{
            fontSize:20,
            fontWeight:700,
            color:"#212121",

        }}>
          Currency Exchange Calculator
        </Typography>
        
        <FormContainer onSubmit={formik.handleSubmit}>
          <FormControl 
              fullWidth 
              size="small" 
              margin="normal" 
              error={formik.touched.source_currency && Boolean(formik.errors.source_currency)}
              sx={{
                '& .MuiInputLabel-root': {
                  color: "#555",
                  fontSize: 12,
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#333"
                  },
                  "&.Mui-focused": {
                    color: "#212121",
                    textTransform: "uppercase",
                    fontWeight: 500
                  }
                },
                '& .MuiOutlinedInput-root': {
                  fontSize: 10,
                  color: "#333",
                  borderRadius: "2px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#212121",
                    borderRadius: "2px"
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontSize: 10,
                  color: 'error.main',
                  textTransform: "capitalize"
                },
              }}>
            <InputLabel id="source-currency-label"
                sx={{
                  color: "#555",
                  fontSize: 12,
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#333"
                  },
                  "&.Mui-focused": {
                    color: "#212121",
                    textTransform: "uppercase",
                    fontWeight: 500
                  }
                }}>Source Currency</InputLabel>
            <Select
              labelId="source-currency-label"
              id="source_currency"
              name="source_currency"
              value={formik.values.source_currency}
              onChange={formik.handleChange}
              error={formik.touched.source_currency && Boolean(formik.errors.source_currency)}
              MenuProps={{
                    PaperProps: {
                      style: {
                        borderRadius: "1px",
                        maxHeight: 200,
                        marginTop: 8,
                        boxShadow: "none",
                        border: "1px solid #e0e0e0",
                        fontSize: 12,
                      },
                    },
                  }}
                  sx={{
                    fontSize: 10,
                    color: "#333",
                    "& .MuiSelect-select": {
                      padding: "8px 14px",
                    },
                  }}
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.code} value={currency.code} 
                sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: "capitalize",
                    padding: "8px 16px",
                    "&:hover": {
                        backgroundColor: "rgb(255, 184, 190)",
                    },
                    "&.Mui-selected": {
                        backgroundColor: "rgb(221, 24, 73)",
                        color: "white",
                    },
                    }}
                          >
                  {currency.code} - {currency.name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.source_currency && formik.errors.source_currency && (
              <Typography color="error" variant="caption">
                {formik.errors.source_currency}
              </Typography>
            )}
          </FormControl>
          
          <FormControl fullWidth margin="normal"
              sx={{
                '& .MuiInputLabel-root': {
                  color: "#555",
                  fontSize: 12,
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#333"
                  },
                  "&.Mui-focused": {
                    color: "#212121",
                    textTransform: "uppercase",
                    fontWeight: 500
                  }
                },
                '& .MuiOutlinedInput-root': {
                  fontSize: 10,
                  color: "#333",
                  borderRadius: "2px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#212121",
                    borderRadius: "2px"
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontSize: 10,
                  color: 'error.main',
                  textTransform: "capitalize"
                },
              }}>
            <InputLabel id="destination-currency-label"
                sx={{
                  color: "#555",
                  fontSize: 12,
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#333"
                  },
                  "&.Mui-focused": {
                    color: "#212121",
                    textTransform: "uppercase",
                    fontWeight: 500
                  }
                }}>Destination Currency</InputLabel>
            <Select
              labelId="destination-currency-label"
              id="destination_currency"
              name="destination_currency"
              value={formik.values.destination_currency}
              onChange={formik.handleChange}
              error={formik.touched.destination_currency && Boolean(formik.errors.destination_currency)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        borderRadius: "1px",
                        maxHeight: 200,
                        marginTop: 8,
                        boxShadow: "none",
                        border: "1px solid #e0e0e0",
                        fontSize: 12,
                      },
                    },
                  }}
                  sx={{
                    fontSize: 10,
                    color: "#333",
                    "& .MuiSelect-select": {
                      padding: "8px 14px",
                    },
                  }}
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.code} value={currency.code}
                    sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    textTransform: "capitalize",
                    padding: "8px 16px",
                    "&:hover": {
                        backgroundColor: "rgb(255, 184, 190)",
                    },
                    "&.Mui-selected": {
                        backgroundColor: "rgb(221, 24, 73)",
                        color: "white",
                    },
                    }}>
                  {currency.code} - {currency.name}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.destination_currency && formik.errors.destination_currency && (
              <Typography color="error" variant="caption">
                {formik.errors.destination_currency}
              </Typography>
            )}
          </FormControl>
          
          <TextField
            fullWidth
            size='small'
            margin="normal"
            id="amount"
            name="destination_amount"
            label="Amount"
            type="number"
            value={formik.values.destination_amount}
            onChange={formik.handleChange}
            error={formik.touched.destination_amount && Boolean(formik.errors.destination_amount)}
            helperText={formik.touched.destination_amount && formik.errors.destination_amount}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {formik.values.source_currency}
                </InputAdornment>
              ),
            }}
              InputLabelProps={{
                sx: {
                  color: "#555", // Default label color
                  fontSize: 12,
                  transition: "color 0.3s ease",
                  "&:hover": {
                      color: "#333"
                  }, // Label color on hover
                  "&.Mui-focused": { 
                      color: "#212121",
                      textTransform:"uppercase",
                      fontWeight:500
                  }, // Label color on focus
                },
              }}
              inputProps={{
                sx: {
                  fontSize: 10, // Field content size
                  color: "#333", // Field content color
                  "&::placeholder": {
                    color: "#aaa", // Placeholder color
                    fontSize: 12,
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: "2px",
                  "&.Mui-focused fieldset": { 
                      borderColor: "#212121",
                      borderRadius:"2px"
                  }, // Focus border
                },
                '& .MuiFormHelperText-root': {
                  fontSize: 10, // Smaller font size for validation errors
                  color: 'error.main', // Red color for errors
                  textTransform:"capitalize"
                },
              
              }}
          />
          
          <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Loading..." : 'Calculate Exchange'}
          </SubmitButton>
        </FormContainer>
        
        {/* {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )} */}
        
        {exchangeRates && (
          <Box sx={{ width: '100%', mt: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Exchange Result
            </Typography>
            <Typography>
              {formik.values.destination_amount} {exchangeRates.destination_currency} = {exchangeRates.amount_receive.toFixed(2)} {exchangeRates.source_currency}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selling Rate: {exchangeRates.selling_rate}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Buying Rate: {exchangeRates.buying_rate}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Amount receive: {exchangeRates.amount_receive}
            </Typography>
            {/* <Typography variant="caption" display="block" color="text.secondary">
              Last updated: {new Date(exchangeRates.timestamp).toLocaleString()}
            </Typography> */}
          </Box>
        )}
      </StyledPaper>
    </Box>
  </Box>
    
  )
}

export default ExchangeRates